// Reading a timestamp that came out of D1.
//
// THE BUG THIS EXISTS TO END. Every timestamp in the database is written by SQLite's
// `datetime('now')` — 128 call sites in the Workers stores, and the handful of JS writers
// deliberately match its shape — which produces `2026-08-13 23:27:49`: **UTC, space
// separated, with no zone marker at all**. `new Date('2026-08-13 23:27:49')` in V8 reads
// that as LOCAL time. In Mountain Daylight Time the dashboard therefore renders every one of
// them six hours in the future.
//
// Kevin saw it as "Built 8/13/2026, 11:27:49 PM" on a line the audit log timestamped
// 5:27:51 PM. The audit log was right because ITS call site happened to append 'Z' by hand.
//
// It is worse than a wrong label. Anything that subtracts from `Date.now()` gets a NEGATIVE
// age, and a negative age passes every "is this recent" test ever written — so the line
// page's Diagnostics rail reported "just now" for a push from last Tuesday. A timestamp that
// is wrong in a plausible direction is the shape of bug this codebase keeps paying for.
//
// WHY A FUNCTION AND NOT A COMMENT. Five call sites had already worked this out and appended
// 'Z' themselves — AuditLog, NotificationBell, ServiceAlerts twice, one field on
// SubscriberDetail — while twenty others had not, and every new screen started from one of
// the twenty. An invariant that lives in a comment gets re-broken by the next code path.
// Nothing in the dashboard should call `new Date()` on an API string again; call `parseTs`.
//
// NOT FOR UNIX EPOCH SECONDS. CDR and graph payloads carry numbers, not strings, and
// `new Date(ts * 1000)` is already unambiguous. This deliberately refuses numbers rather
// than guessing at seconds-vs-milliseconds.

/** Does this string already say which zone it is in? */
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;
/** D1's shape: `YYYY-MM-DD HH:MM:SS`, optionally with fractional seconds. */
const D1_SHAPE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * An API timestamp as a Date, or null when there isn't one.
 *
 * Null in, null out — every caller has a "—" branch and none of them wants `Invalid Date`
 * rendered into it.
 */
export function parseTs(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // A number is epoch seconds or milliseconds and this cannot tell which. Refuse.
  if (typeof value !== 'string') return null;

  const raw = value.trim();
  // Zone-bearing (a real ISO string) or a bare date: JS already reads both as UTC.
  const normalised = HAS_ZONE.test(raw) || !D1_SHAPE.test(raw)
    ? raw
    : `${raw.replace(' ', 'T')}Z`;

  const d = new Date(normalised);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date and time in the viewer's own zone — the default for "when did this happen". */
export function fmtDateTime(value, fallback = null) {
  const d = parseTs(value);
  return d ? d.toLocaleString() : fallback;
}

/** Date alone, for things where the time of day is noise (created, cancelled, expires). */
export function fmtDate(value, fallback = null) {
  const d = parseTs(value);
  return d ? d.toLocaleDateString() : fallback;
}

/**
 * How long ago, in words. Coarse on purpose: this answers "is this fresh" and nothing else.
 *
 * A timestamp in the FUTURE is reported as such rather than rounded down to "just now",
 * because that rounding is what hid the parsing bug above for months — every stale push read
 * as current. Clock skew of a few seconds is normal and stays "just now"; anything beyond a
 * minute ahead is something being wrong, and saying so is the point.
 */
export function ago(value, { now = Date.now() } = {}) {
  const d = parseTs(value);
  if (!d) return null;
  const mins = Math.round((now - d.getTime()) / 60_000);
  if (mins < -1) return 'in the future';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} h ago`;
  return d.toLocaleDateString();
}
